import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import WebsiteNavbar from '../components/website/WebsiteNavbar';
import WebsiteFooter from '../components/website/WebsiteFooter';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  title: string;
  items: FaqItem[];
}

const faqData: FaqCategory[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is CricClash?',
        answer: 'CricClash is a fantasy cricket platform where you create or join contests, build your dream team from real cricket players, and earn points based on their real-match performances. Compete with friends on leaderboards across entire cricket series.',
      },
      {
        question: 'Is CricClash free to use?',
        answer: 'Yes! Many series are completely free with all features unlocked. Some series offer tiered plans (Starter, Pro, Elite, Ultimate) that unlock additional features like transfers, auctions, and advanced scoring. Contest owners choose the plan when creating a contest.',
      },
      {
        question: 'How do I create an account?',
        answer: 'You can sign up with your email and password, or use Google Sign-In for a quicker setup. After registration, verify your email to get started.',
      },
      {
        question: 'Is CricClash a betting or gambling platform?',
        answer: 'No. CricClash is a skill-based fantasy sports platform. There is no real-money wagering. You compete based on cricket knowledge and team-building strategy.',
      },
    ],
  },
  {
    title: 'Contests',
    items: [
      {
        question: 'How do I create a contest?',
        answer: 'Go to "Create Contest" from the home page. Select a cricket series, name your contest, and choose a plan tier (or skip if the series is free). After creation, configure rules and scoring in the Edit Rules page, then publish to generate a join code.',
      },
      {
        question: 'How do I join a contest?',
        answer: 'Click "Join Contest" and enter the 6-character contest code shared by the contest owner. Your join request will be sent to the owner for approval. Once accepted, you can access the contest and create your team.',
      },
      {
        question: 'What happens after I request to join?',
        answer: 'The contest owner receives a notification and can accept or reject your request from the Members tab. You\'ll receive a notification when your request is processed. While pending, you cannot access the contest details.',
      },
      {
        question: 'Can I join multiple contests?',
        answer: 'Yes, you can join as many contests as you like across different series. Each contest is independent with its own rules, scoring, and leaderboard.',
      },
      {
        question: 'What does publishing a contest do?',
        answer: 'Publishing locks the rules and scoring configuration, generates a unique join code, and makes the contest available for members to join. Rules cannot be changed after publishing.',
      },
    ],
  },
  {
    title: 'Teams & Players',
    items: [
      {
        question: 'How do I build my fantasy team?',
        answer: 'Go to your contest\'s Teams tab and click "Create Team". Select players from the series squads according to the contest rules (team size, role limits, etc.). Choose a Captain and Vice-Captain for bonus point multipliers.',
      },
      {
        question: 'What are Captain and Vice-Captain?',
        answer: 'The Captain earns a bonus multiplier on their fantasy points (set by the contest owner, typically 2x). The Vice-Captain earns a smaller multiplier (typically 1.5x). Choosing these wisely can significantly impact your score.',
      },
      {
        question: 'Can I edit my team after creating it?',
        answer: 'Yes, you can edit your team until the match deadline (when teams lock). After that, a lineup snapshot is taken and used for scoring. If transfers are enabled, you can swap players between matches during transfer windows.',
      },
      {
        question: 'What are role limits?',
        answer: 'Contest owners can set minimum and maximum limits for each player role — Batsmen, Bowlers, All-rounders, and Wicket-keepers. For example, a contest might require at least 3 bowlers and maximum 4 batsmen.',
      },
      {
        question: 'How does player selection work?',
        answer: 'CricClash supports three selection formats: Open Selection (pick any players freely), Snake Draft (take turns picking), and Auction (bid on players with virtual currency). The format is set by the contest owner.',
      },
    ],
  },
  {
    title: 'Scoring & Points',
    items: [
      {
        question: 'How are fantasy points calculated?',
        answer: 'Points are based on real match performances — runs scored, wickets taken, catches, stumpings, run-outs, and more. Each contest has its own scoring system configured by the owner, so points may vary between contests.',
      },
      {
        question: 'Is there a standard scoring system?',
        answer: 'No — every contest can have completely different scoring rules. Contest owners configure the exact point values for every action (batting, bowling, fielding), bonus/penalty thresholds (like strike rate and economy rate), and multipliers. Check your contest\'s Rules tab to see the scoring system.',
      },
      {
        question: 'When are points updated?',
        answer: 'Fantasy points are automatically calculated and updated after each match in the series is completed. You\'ll receive a notification when points are updated for your contest.',
      },
      {
        question: 'What if a player doesn\'t play in a match?',
        answer: 'Players who don\'t participate in a match earn 0 fantasy points for that match. Their inclusion in your team doesn\'t penalize you, but you miss out on potential points from an active player.',
      },
    ],
  },
  {
    title: 'Transfers',
    items: [
      {
        question: 'What are transfers?',
        answer: 'Transfers let you swap players in your team between matches. If the contest owner has enabled transfers, you can remove players and add replacements during active transfer windows.',
      },
      {
        question: 'How do transfer windows work?',
        answer: 'Transfer windows are configured by the contest owner. They can be set for the entire series, before every match, during the mid-series, or a custom date range. Each window can have a maximum number of transfers allowed.',
      },
      {
        question: 'Can I change my Captain during a transfer?',
        answer: 'Yes, if the contest allows captain changes. You can update your Captain and Vice-Captain alongside player transfers, or independently if captain changes are enabled without transfers.',
      },
      {
        question: 'What happens when teams lock?',
        answer: 'Before each match deadline, teams are automatically locked and a lineup snapshot is taken. This snapshot is used for scoring that match. Any transfers made after locking apply to future matches only.',
      },
    ],
  },
  {
    title: 'Leaderboard & Rankings',
    items: [
      {
        question: 'How does the leaderboard work?',
        answer: 'The leaderboard ranks all teams in a contest by total fantasy points accumulated across all completed matches. It updates automatically after each match\'s points are calculated.',
      },
      {
        question: 'Can I see match-by-match breakdowns?',
        answer: 'Yes. Go to the Points tab in your contest to see every player\'s fantasy points broken down by match, with detailed sections for batting, bowling, and fielding contributions.',
      },
      {
        question: 'What is "Best Of" scoring?',
        answer: 'Some contests use "Best Of N" scoring where only your top N performing players\' points count toward your total, rather than all players. This is configured by the contest owner.',
      },
    ],
  },
  {
    title: 'Account & Security',
    items: [
      {
        question: 'How do I reset my password?',
        answer: 'Click "Forgot Password" on the login screen, enter your registered email, and you\'ll receive a verification code. Use it to set a new password.',
      },
      {
        question: 'Is my personal information safe?',
        answer: 'Yes. CricClash uses industry-standard encryption and AWS Cognito for authentication. We do not share your personal data with third parties.',
      },
      {
        question: 'Can I use Google Sign-In?',
        answer: 'Yes, CricClash supports Google Sign-In for both the mobile app and web. It\'s the quickest way to create an account and log in.',
      },
    ],
  },
  {
    title: 'Credits & Plans',
    items: [
      {
        question: 'What are credits?',
        answer: 'Credits are the in-app currency used to create contests with premium plan tiers and add-on features. Free series don\'t require any credits.',
      },
      {
        question: 'How do I buy credits?',
        answer: 'On the web, go to the Credits page and purchase a credit package via Stripe (card, Google Pay, Apple Pay). On mobile, credits are purchased through the App Store or Google Play.',
      },
      {
        question: 'What are plan tiers?',
        answer: 'When creating a contest, you choose a plan tier (Starter, Pro, Elite, Ultimate). Higher tiers unlock more features like transfers, auctions, advanced scoring, and custom rules. Some series are marked as "Free" where all features are unlocked without any cost.',
      },
    ],
  },
];

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<string | null>('0-0');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<number | null>(null);

  const filteredData = useMemo(() => {
    if (!search.trim()) return activeCategory !== null ? [faqData[activeCategory]] : faqData;
    const q = search.toLowerCase();
    return faqData
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) => item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [search, activeCategory]);

  const totalResults = filteredData.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <div className="min-h-screen bg-[#0F1114]">
      <WebsiteNavbar />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-block px-3 py-1 bg-[#13EC5B]/10 border border-[#13EC5B]/20 rounded-full text-[#13EC5B] text-xs font-semibold mb-4 uppercase tracking-wider">
              FAQ
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Help Center</h1>
            <p className="text-gray-400 max-w-lg mx-auto">
              Everything you need to know about CricClash fantasy cricket.
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setActiveCategory(null); }}
              placeholder="Search questions..."
              className="w-full pl-11 pr-4 py-3.5 bg-[#1A1D23] border border-[#2E3340] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#13EC5B]/40 transition-colors"
            />
            {search && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                {totalResults} result{totalResults !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Category pills */}
          {!search && (
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  activeCategory === null
                    ? 'bg-[#13EC5B]/15 text-[#13EC5B] border border-[#13EC5B]/30'
                    : 'bg-[#1A1D23] text-gray-400 border border-[#2E3340] hover:border-[#13EC5B]/20'
                }`}
              >
                All
              </button>
              {faqData.map((cat, i) => (
                <button
                  key={i}
                  onClick={() => setActiveCategory(activeCategory === i ? null : i)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    activeCategory === i
                      ? 'bg-[#13EC5B]/15 text-[#13EC5B] border border-[#13EC5B]/30'
                      : 'bg-[#1A1D23] text-gray-400 border border-[#2E3340] hover:border-[#13EC5B]/20'
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>
          )}

          {/* FAQ sections */}
          {filteredData.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg mb-2">No results found</p>
              <p className="text-gray-600 text-sm">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredData.map((category, catIdx) => {
                const globalCatIdx = faqData.indexOf(category) >= 0 ? faqData.indexOf(category) : catIdx;
                return (
                  <div key={category.title}>
                    <h2 className="text-xs font-semibold text-[#13EC5B] uppercase tracking-wider mb-3 px-1">{category.title}</h2>
                    <div className="space-y-2">
                      {category.items.map((faq, itemIdx) => {
                        const key = `${globalCatIdx}-${itemIdx}`;
                        const isOpen = openIndex === key;
                        return (
                          <div key={key} className="bg-[#1A1D23] border border-[#2E3340] rounded-xl overflow-hidden">
                            <button
                              onClick={() => setOpenIndex(isOpen ? null : key)}
                              className="w-full flex items-center justify-between p-4 text-left group"
                            >
                              <span className={`text-sm font-semibold pr-4 transition-colors ${isOpen ? 'text-[#13EC5B]' : 'text-white group-hover:text-[#13EC5B]'}`}>
                                {faq.question}
                              </span>
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`text-xs flex-shrink-0 transition-all ${isOpen ? 'text-[#13EC5B] rotate-180' : 'text-gray-500'}`}
                              />
                            </button>
                            {isOpen && (
                              <div className="px-4 pb-4 pt-0">
                                <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Contact */}
          <div className="mt-14 bg-[#1A1D23] border border-[#2E3340] rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
            <p className="text-gray-400 mb-6">Can't find what you're looking for? Reach out and we'll help.</p>
            <a
              href="mailto:support@cricclash.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#13EC5B] text-black rounded-xl font-bold text-sm hover:bg-[#10D450] transition-all"
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Email Support
            </a>
          </div>
        </div>
      </div>

      <WebsiteFooter />
    </div>
  );
};

export default FAQPage;
